import { Container, Graphics, Text, Sprite } from 'pixi.js';
import { AssetLoader } from './AssetLoader';

export class GameOverScreen extends Container {
  private background!: Graphics;
  private onClickCallback?: () => void;
  private gameOverLogo!: Sprite;
  private finalScoreText!: Text;
  private finalTimeText!: Text;
  private bestScoreText!: Text;

  constructor(gameWidth: number, gameHeight: number) {
    super();

    this.createBackground(gameWidth, gameHeight);
    this.createText(gameWidth, gameHeight);
    this.setupClickHandler();

    this.visible = false;
  }

  private createBackground(gameWidth: number, gameHeight: number): void {
    this.background = new Graphics();
    this.background.rect(0, 0, gameWidth, gameHeight);
    this.background.fill({ color: 0x000000, alpha: 0.35 });
    this.background.eventMode = 'static';
    this.background.cursor = 'pointer';
    this.addChild(this.background);
  }

  private setupClickHandler(): void {
    this.background.on('pointerdown', () => {
      if (this.onClickCallback) {
        this.onClickCallback();
      }
    });
  }

  private createText(gameWidth: number, gameHeight: number): void {
    const assetLoader = AssetLoader.getInstance();
    const gameOverTexture = assetLoader.getTexture('gameover');

    this.gameOverLogo = new Sprite(gameOverTexture);
    this.gameOverLogo.anchor.set(0.5);
    this.gameOverLogo.x = gameWidth / 2;
    this.gameOverLogo.y = gameHeight * 0.3;

    // Scale the logo to fit nicely
    const maxWidth = gameWidth * 0.5;
    const maxHeight = 150;
    const scale = Math.min(maxWidth / this.gameOverLogo.width, maxHeight / this.gameOverLogo.height);
    this.gameOverLogo.scale.set(scale);

    this.addChild(this.gameOverLogo);

    this.finalScoreText = new Text({
      text: 'Score: 0',
      style: {
        fontFamily: 'Rubik',
        fontSize: 28,
        fontWeight: 'bold',
        fill: 0xffd700,
        align: 'center',
      },
    });

    this.finalScoreText.anchor.set(0.5);
    this.finalScoreText.x = gameWidth / 2;
    this.finalScoreText.y = gameHeight * 0.65;

    this.addChild(this.finalScoreText);

    this.finalTimeText = new Text({
      text: 'Time: 0.0s',
      style: {
        fontFamily: 'Rubik',
        fontSize: 22,
        fontWeight: 'normal',
        fill: 0xffffff,
        align: 'center',
      },
    });

    this.finalTimeText.anchor.set(0.5);
    this.finalTimeText.x = gameWidth / 2;
    this.finalTimeText.y = gameHeight * 0.72;

    this.addChild(this.finalTimeText);

    this.bestScoreText = new Text({
      text: 'Best: 0',
      style: {
        fontFamily: 'Rubik',
        fontSize: 20,
        fontWeight: 'normal',
        fill: 0xaaaaaa,
        align: 'center',
      },
    });

    this.bestScoreText.anchor.set(0.5);
    this.bestScoreText.x = gameWidth / 2;
    this.bestScoreText.y = gameHeight * 0.78;

    this.addChild(this.bestScoreText);
  }

  show(finalScore: number, finalTime: string, bestScore: number): void {
    this.finalScoreText.text = `Score: ${finalScore}`;
    this.finalTimeText.text = `Time: ${finalTime}`;
    this.bestScoreText.text = `Best: ${bestScore}`;
    this.visible = true;
  }

  hide(): void {
    this.visible = false;
  }

  onClick(callback: () => void): void {
    this.onClickCallback = callback;
  }

  updateDimensions(gameWidth: number, gameHeight: number): void {
    if (this.background) {
      this.background.clear();
      this.background.rect(0, 0, gameWidth, gameHeight);
      this.background.fill({ color: 0x000000, alpha: 0.7 });
    }

    if (this.gameOverLogo) {
      this.gameOverLogo.x = gameWidth / 2;
      this.gameOverLogo.y = gameHeight * 0.3;

      const maxWidth = gameWidth * 0.5;
      const maxHeight = 150;
      const scale = Math.min(maxWidth / this.gameOverLogo.texture.width, maxHeight / this.gameOverLogo.texture.height);
      this.gameOverLogo.scale.set(scale);
    }

    if (this.finalScoreText) {
      this.finalScoreText.x = gameWidth / 2;
      this.finalScoreText.y = gameHeight * 0.65;
    }

    if (this.finalTimeText) {
      this.finalTimeText.x = gameWidth / 2;
      this.finalTimeText.y = gameHeight * 0.72;
    }

    if (this.bestScoreText) {
      this.bestScoreText.x = gameWidth / 2;
      this.bestScoreText.y = gameHeight * 0.78;
    }
  }
}
